/**
 * CANVMS Incidents API
 * API functions for managing VMS incidents
 */

import canvmsApi from './apiClient';

// Types
export type IncidentSeverity = 'low' | 'medium' | 'high' | 'critical';
export type IncidentStatus = 'new' | 'reviewing' | 'dispatched' | 'resolved' | 'closed' | 'dismissed';

export interface Incident {
  id: number;
  site_id: number;
  camera_id: number | null;
  ai_event_id: number | null;
  incident_type: string;
  severity: IncidentSeverity;
  status: IncidentStatus;
  title: string;
  description: string | null;
  clip_start_time: string | null;
  clip_end_time: string | null;
  thumbnail_url: string | null;
  clip_url: string | null;
  created_by: number | null;
  assigned_to: number | null;
  reviewed_by: number | null;
  reviewed_at: string | null;
  resolved_by: number | null;
  resolved_at: string | null;
  resolution_notes: string | null;
  resolution_reason: string | null;
  patrol_id: number | null;
  checklist_id: number | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  camera_name?: string | null;
  camera_display_name?: string | null;
  site_name?: string | null;
  site_display_name?: string | null;
  customer_id?: number | null;
  address_id?: number | null;
  customer_name?: string | null;
  address_title?: string | null;
  created_by_name?: string | null;     // joined from contacts — may not be returned yet by backend
}

export interface IncidentCounts {
  new: number;
  reviewing: number;
  dispatched: number;
  resolved: number;
  closed: number;
  dismissed: number;
}

export interface IncidentFilters {
  status?: string;
  severity?: string;
  cameraId?: number;
  siteId?: number;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export interface IncidentNote {
  id: number;
  incident_id: number;
  user_id: number;
  note: string;
  created_at: string;
  user_name?: string;
}

export interface IncidentTimelineEntry {
  id: number;
  incident_id: number;
  action: 'created' | 'status_changed' | 'note_added' | 'dispatched' | 'assigned';
  from_status?: IncidentStatus | null;
  to_status?: IncidentStatus | null;
  user_id: number | null;
  user_name?: string;
  details?: string | null;
  created_at: string;
}

export interface IncidentDetail extends Incident {
  notes?: IncidentNote[];
  timeline?: IncidentTimelineEntry[];
  ai_event?: {
    id: number;
    event_type: string;
    details: string | null;
  } | null;
  patrol?: {
    id: number;
    status: string;
    guard_name?: string;
    start_time?: string;
  } | null;
}

export interface CreateIncidentInput {
  cameraId: number;
  siteId: number;
  incidentType: string;
  severity?: IncidentSeverity;
  title: string;
  description?: string;
  clipStartTime?: string;
  clipEndTime?: string;
  eventId?: number;
}

export interface DispatchIncidentInput {
  guardId: number;
  startTime: string;
  durationHours?: number;
  instructions?: string;
  notifySupervisor?: boolean;
}

// List incidents
export const getIncidents = async (
  filters?: IncidentFilters
): Promise<{ data: { incidents: Incident[]; total: number; counts: IncidentCounts } }> => {
  const { data } = await canvmsApi.get('/incidents', { params: filters });
  return data;
};

// Get single incident with details
export const getIncident = async (
  incidentId: number
): Promise<{ data: IncidentDetail }> => {
  const { data } = await canvmsApi.get(`/incidents/${incidentId}`);
  return data;
};

// Create manual incident
export const createIncident = async (
  input: CreateIncidentInput
): Promise<{ data: Incident }> => {
  const { data } = await canvmsApi.post('/incidents', input);
  return data;
};

// Update incident status
export const updateIncidentStatus = async (
  incidentId: number,
  status: IncidentStatus,
  notes?: string,
): Promise<{ data: Incident }> => {
  const { data } = await canvmsApi.patch(`/incidents/${incidentId}/status`, {
    status,
    notes,
  });
  return data;
};

// Add note to incident
export const addIncidentNote = async (
  incidentId: number,
  note: string
): Promise<{ data: IncidentNote }> => {
  const { data } = await canvmsApi.post(`/incidents/${incidentId}/notes`, { note });
  return data;
};

// Dispatch patrol from incident
export const dispatchIncident = async (
  incidentId: number,
  input: DispatchIncidentInput
): Promise<{ data: { incident: Incident; patrolId: number } }> => {
  const { data } = await canvmsApi.post(`/incidents/${incidentId}/dispatch`, input);
  return data;
};

// Map data types
export interface VmsIncidentMapData {
  id: number;
  incident_type: string;
  severity: IncidentSeverity;
  status: string;
  title: string;
  thumbnail_url: string | null;
  created_at: string;
  patrol_id: number | null;
  latitude: number;
  longitude: number;
  site_name: string;
  site_display_name: string | null;
  camera_name: string | null;
  camera_display_name: string | null;
}

// Get active incidents for map display
export const getIncidentsForMap = async (): Promise<VmsIncidentMapData[]> => {
  const { data } = await canvmsApi.get('/map/incidents');
  return data.data;
};

// Checklist mapping types
export interface ChecklistMapping {
  id: number;
  incident_type: string;
  checklist_template_id: number;
  enabled: boolean;
  created_at: string;
  template_name: string | null;
}

export interface ChecklistTemplate {
  ChecklistID: number;
  Name: string;
  Description: string | null;
  checklist_type: string | null;
  checkpoint_count: number;
}

export interface CheckpointItem {
  CheckpointID: number;
  Name: string;
  Description: string | null;
  ResponseTypeID: number;
  Sort: number;
  response_type_name: string | null;
  selected_status_name: string | null;
  Comment: string | null;
  SubmittedDate: string | null;
  is_completed: number;
}

// Checklist mapping CRUD
export const getChecklistMappings = async (): Promise<{ data: ChecklistMapping[] }> => {
  const { data } = await canvmsApi.get('/incident-checklist-map');
  return data;
};

export const setChecklistMapping = async (
  incidentType: string,
  checklist_template_id: number,
  enabled = true
): Promise<{ data: ChecklistMapping }> => {
  const { data } = await canvmsApi.put(`/incident-checklist-map/${encodeURIComponent(incidentType)}`, {
    checklist_template_id,
    enabled,
  });
  return data;
};

export const deleteChecklistMapping = async (
  incidentType: string
): Promise<void> => {
  await canvmsApi.delete(`/incident-checklist-map/${encodeURIComponent(incidentType)}`);
};

export const getChecklistTemplates = async (): Promise<{ data: ChecklistTemplate[] }> => {
  const { data } = await canvmsApi.get('/checklist-templates');
  return data;
};

// Incident checklist data
export interface IncidentChecklistData {
  checklist: {
    ChecklistID: number;
    Name: string;
    Description: string | null;
  } | null;
  checkpoints: CheckpointItem[];
  progress: {
    total: number;
    completed: number;
  };
}

export const getIncidentChecklist = async (
  incidentId: number
): Promise<{ data: IncidentChecklistData }> => {
  const { data } = await canvmsApi.get(`/incidents/${incidentId}/checklist`);
  return data;
};

// Guard availability types
export interface GuardAvailabilityPatrol {
  scheduleAssignmentId: string;
  customerId: number;
  customerName: string;
  startTime: string;
  endTime: string;
}

export interface GuardAvailability {
  guardId: number;
  date: string;
  patrols: GuardAvailabilityPatrol[];
  nextAvailableWindow: { start: string; end: string } | null;
  hasConflict: boolean;
  conflictingPatrols: GuardAvailabilityPatrol[];
}

export const getGuardAvailability = async (
  guardId: number,
  date: string,
  startTime?: string,
  durationHours?: number
): Promise<{ data: GuardAvailability }> => {
  const params: Record<string, string> = { guardId: String(guardId), date };
  if (startTime) params.startTime = startTime;
  if (durationHours) params.durationHours = String(durationHours);
  const { data } = await canvmsApi.get('/dispatch/guard-availability', { params });
  return data;
};
